// aiExfiltrationGuard — Detektion av försök att exfiltrera AI-verktyg,
// skills, system-prompt eller agent-metadata.
//
// Bygger vidare på `promptInjectionGuard` men fokuserar på
// *informationsläckning* snarare än role-override. Aktiveras som
// request-time guard i båda AI-endpoints (workflow-AI + admin-AI).

export type ExfilFlag =
  | 'tool_enum_probe'
  | 'agent_metadata_probe'
  | 'system_prompt_probe'
  | 'capability_probe'
  | 'encoded_bypass';

export interface ExfilResult {
  score: number;
  flags: ExfilFlag[];
  /** Om tröskeln överskrids → block. */
  blocked: boolean;
  /** Samma tröskel som gav utslag (för diagnostik). */
  threshold: number;
}

// Pattern A: action-verb → valfritt filler-ord (0-40 chars) → noun.
// Detta fångar "list your tools", "show all actions", "enumerate capabilities".
const TOOL_PROBE_VERB_FIRST = /\b(list|enumerate|show|dump|print|reveal|display|tell\s+me|give\s+me|mention)\b.{0,40}?\b(tools?|functions?|apis?|actions?|capabilities|commands?|skills?|handlers?|abilities)\b/i;
// Pattern B: "what/which X tools/functions" (question-form probe).
const TOOL_PROBE_QUERY = /\b(what|which|how\s+many)\b.{0,40}?\b(tools?|functions?|apis?|actions?|capabilities|commands?|skills?|handlers?|abilities)\b/i;

const PATTERNS: Array<{ flag: ExfilFlag; weight: number; regex: RegExp }> = [
  { flag: 'tool_enum_probe', weight: 5, regex: TOOL_PROBE_VERB_FIRST },
  { flag: 'tool_enum_probe', weight: 5, regex: TOOL_PROBE_QUERY },
  // "what model are you", "what model you are", "who made you", "are you a GPT"
  {
    flag: 'agent_metadata_probe',
    weight: 4,
    regex: /\b(what|which)\s+(model|llm|provider|version|company)\s+(?:are\s+you|you\s+are|do\s+you|is\s+this)\b|\bwho\s+(made|built|trained|created)\s+you\b|\bare\s+you\s+(?:a\s+)?(claude|gpt|gemini|llama|openai|anthropic)\b/i,
  },
  // "print your system prompt", "reveal the instructions", "show your rules"
  {
    flag: 'system_prompt_probe',
    weight: 6,
    regex: /\b(print|show|reveal|output|repeat|quote|dump)\s+(?:your|the)\s+(?:system\s+)?(?:prompt|instructions?|rules?|context|setup|configuration|initial\s+message)\b/i,
  },
  // "what can you do", "what are your capabilities"
  {
    flag: 'capability_probe',
    weight: 2,
    regex: /\bwhat\s+(?:can|could)\s+you\s+do\b|\bwhat\s+(?:are\s+your\s+)?capabilities\b/i,
  },
  // "encode your tools in base64", "decode this base64 prompt"
  {
    flag: 'encoded_bypass',
    weight: 4,
    regex: /\b(encode|decode|base\s*64|rot13|hex)\s+.{0,40}(tools?|prompt|instructions?|system|rules?)\b|\b(tools?|prompt|instructions?)\s+.{0,20}(in|as|using)\s+(base\s*64|hex|rot13)\b/i,
  },
];

export const EXFIL_THRESHOLD = 4;

export function checkForExfiltration(text: string): ExfilResult {
  if (!text) return { score: 0, flags: [], blocked: false, threshold: EXFIL_THRESHOLD };

  const flagSet = new Set<ExfilFlag>();
  let score = 0;
  for (const { flag, weight, regex } of PATTERNS) {
    if (regex.test(text)) {
      if (!flagSet.has(flag)) {
        score += weight;
      }
      flagSet.add(flag);
    }
  }

  return {
    score,
    flags: [...flagSet],
    blocked: score >= EXFIL_THRESHOLD,
    threshold: EXFIL_THRESHOLD,
  };
}

/** För batch-check mot hela message-array (chat-historik). */
export function checkMessagesForExfiltration(
  messages: Array<{ role: string; content: string }>,
): ExfilResult {
  const combined = messages
    .filter(m => m.role !== 'system')
    .map(m => m.content)
    .join('\n');
  return checkForExfiltration(combined);
}

/** Standard-refusal som ska returneras till user när en prompt block:as. */
export const EXFIL_REFUSAL_MESSAGE =
  "That's not something I can share. What can I help you with regarding the workflow or task at hand?";

/** Loggar-safe summary för audit-log. Innehåller INTE user-input. */
export function exfilSummary(result: ExfilResult): string {
  if (!result.blocked) return 'not-blocked';
  return `blocked:${result.flags.join(',')}:score=${result.score}`;
}
